import React, { useState, useMemo, useEffect } from 'react';
import { useExcelExport } from '../hooks/useExcelExport';
import { useGoogleSheetsSync } from '../hooks/useGoogleSheetsSync';
import { getYear, getMonth } from 'date-fns';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const ExcelExportPanel = ({ transactions, defaultMinimized = false }) => {
  const [isExpanded, setIsExpanded] = useState(!defaultMinimized);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  
  const { 
    exportMonth,
    formatCurrency 
  } = useExcelExport();
  
  const {
    syncTransactions,
    syncing,
    lastSync,
    syncError,
    clearError
  } = useGoogleSheetsSync();
  
  // Obtener años disponibles
  const availableYears = [...new Set(transactions.map(t => getYear(new Date(t.date))))].sort((a, b) => b - a);

  // Obtener meses disponibles para el año seleccionado
  const availableMonths = useMemo(() => {
    const yearTransactions = transactions.filter(t => getYear(new Date(t.date)) === selectedYear);
    const months = [...new Set(yearTransactions.map(t => getMonth(new Date(t.date))))].sort((a, b) => a - b);
    console.log('ExcelExport - Available months calculated:', months);
    return months;
  }, [transactions, selectedYear]);

  // Asegurar que el año y mes estén disponibles
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
    
    if (availableMonths.length > 0 && !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableYears, availableMonths, selectedYear, selectedMonth]);

  const handleExportMonth = () => {
    // Exportar transacciones del mes específico
    const monthTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      return getYear(date) === selectedYear && getMonth(date) === selectedMonth;
    });
    
    if (monthTransactions.length > 0) {
      exportMonth(transactions, selectedYear, selectedMonth);
    } else {
      alert('Não há transações para o mês selecionado');
    }
  };

  const handleSyncGoogleSheets = async () => {
    try {
      if (syncError) clearError();
      
      const result = await syncTransactions(transactions);
      
      if (result.success) {
        alert(`Sincronização bem-sucedida com Google Sheets!\n\n` +
              `Transações processadas: ${result.totalTransactions}\n` +
              `Meses processados: ${result.monthsProcessed}\n` +
              `Sincronizado: ${new Date().toLocaleString()}`);
      } else {
        alert(`Erro na sincronização: ${result.error}`);
      }
    } catch (error) {
      alert(`Erro inesperado: ${error.message}`);
    }
  };



  return (
    <div className="expense-card">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Exportação e Sincronização
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Exporta Excel ou sincroniza com Google Sheets
          </p>
          {lastSync && (
            <p className="text-xs text-green-600 mt-1">
              Última sincronização: {lastSync.toLocaleString()}
            </p>
          )}
        </div>
        <button 
          type="button"
          className="button-secondary text-xl"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-4">
          
          {/* Información sobre guardado automático */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-medium text-green-900 mb-2">Salvamento Automático Ativo</h3>
            <p className="text-sm text-green-800">
              Cada transação é salva automaticamente em arquivos Excel organizados por ano usando o formato do template.
              Os arquivos são baixados quando você adiciona novas transações.
            </p>
          </div>

          {/* Resumen de archivos por año */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Resumo de Arquivos Disponíveis:</h3>
            
            {availableYears.length === 0 ? (
              <p className="text-gray-500 text-sm">Não há transações para exportar</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {availableYears.map((year) => {
                  const yearTransactions = transactions.filter(t => getYear(new Date(t.date)) === year);
                  const totalAmount = yearTransactions.reduce((sum, t) => sum + t.gbpAmount, 0);
                  
                  return (
                    <div key={year} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-medium text-gray-900">{year}</div>
                      <div className="text-sm text-gray-600">
                        {yearTransactions.length} transações
                      </div>
                      <div className="text-sm font-medium text-green-600">
                        Total: {formatCurrency(totalAmount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sincronización Google Sheets */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-medium text-gray-900 mb-3">Sincronização Google Sheets</h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-blue-900">Sincronização Automática</h4>
                  <p className="text-sm text-blue-800">
                    Sincroniza todas as suas transações com Google Sheets automaticamente.
                    Serão criadas planilhas por mês com a mesma estrutura dos seus relatórios Excel.
                  </p>
                  {syncError && (
                    <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                      Error: {syncError}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleSyncGoogleSheets}
                  disabled={syncing || transactions.length === 0}
                  className="button-primary disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
                >
                  {syncing ? (
                    <>Sincronizando...</>
                  ) : (
                    <>Sincronizar Tudo</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Exportación manual */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-medium text-gray-900 mb-3">Exportação Manual (Excel)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ano
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="select-field"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mês
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="select-field"
                >
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{MONTH_NAMES[month]}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <button
                  onClick={handleExportMonth}
                  disabled={availableMonths.length === 0}
                  className="button-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Exportar Excel
                </button>
              </div>
            </div>
            
            {availableYears.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Não há transações disponíveis para exportar
              </p>
            )}
          </div>

          {/* Información adicional */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-medium text-yellow-900 mb-2">Informações do Template:</h3>
            <p className="text-sm text-yellow-800 mb-3">
              Por razões de segurança do navegador, não é possível carregar arquivos Excel locais diretamente. 
              O sistema usa um template básico com a estrutura correta.
            </p>
            <h4 className="font-medium text-yellow-900 mb-2">Características do Excel gerado:</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• <strong>Organização por dia do mês</strong> (linhas 1-31)</li>
              <li>• <strong>Colunas por categoria</strong>: Food, Go Out, Bills, Amanda, Martin, Holidays, House</li>
              <li>• <strong>Valores e referências separadas</strong> por cada categoria</li>
              <li>• <strong>Referências combinadas</strong>: "Nome (valor) + Nome (valor)"</li>
              <li>• <strong>Conversões automáticas</strong> para GBP incluídas</li>
              <li>• <strong>Estrutura compatível</strong> com seu template original</li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
};

export default ExcelExportPanel; 